import { useCallback, useMemo } from 'react';
import { InteractionStatus, type AccountInfo, type RedirectRequest } from '@azure/msal-browser';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';

import { appConfig } from '@/config/appConfig';
import { clearAttested } from './attestationSession';
import { loginRequest } from './msalConfig';

/** 10-digit NPI. */
const NPI_RE = /^\d{10}$/;
/** Full directory-extension claim name, e.g. extension_<appId>_npi. */
const NPI_EXTENSION_CLAIM_RE = /^extension_[0-9a-f]{32}_npi$/i;

/**
 * Provider NPI taken straight from the signed-in user's ID token claims — the
 * same directory-extension the API reads from the access token, so the SPA can
 * display it from login alone without an API round-trip. Accepts the optional-
 * claim short form `extn.npi` or the full `extension_<appId>_npi` name.
 */
function npiFromClaims(claims: Record<string, unknown> | undefined): string | null {
  if (!claims) return null;
  const raw: unknown[] = [claims['extn.npi']];
  for (const [key, value] of Object.entries(claims)) {
    if (NPI_EXTENSION_CLAIM_RE.test(key)) raw.push(value);
  }
  // Entra emits directory-extension claims as a string OR a single-element
  // array (e.g. extn.npi = ["1568663185"]), so flatten one level before match.
  const candidates = raw.flatMap((v) => (Array.isArray(v) ? v : [v]));
  for (const candidate of candidates) {
    if (typeof candidate === 'string' && NPI_RE.test(candidate.trim())) {
      return candidate.trim();
    }
  }
  return null;
}

export interface AuthUser {
  /** Best-effort display name. */
  name: string;
  /** Username / UPN / email. */
  username: string;
  /** Initials suitable for an avatar fallback (max 2 chars). */
  initials: string;
  /** Stable account identifier from MSAL. */
  homeAccountId: string;
  /** Provider NPI from the ID token directory-extension claim, or null. */
  npi: string | null;
  /** Raw MSAL account, in case callers need to forward it. */
  account: AccountInfo;
}

export interface UseAuthResult {
  isAuthenticated: boolean;
  isInteractionInProgress: boolean;
  user: AuthUser | null;
  signIn: (overrides?: Partial<RedirectRequest>) => Promise<void>;
  signOut: () => Promise<void>;
}

const buildInitials = (name: string, username: string): string => {
  const source = (name || username || '').trim();
  if (!source) return '?';
  const parts = source.split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

/**
 * Strongly-typed convenience hook around MSAL React. Components should prefer
 * this over `useMsal()` directly so that auth state shape stays consistent.
 */
export function useAuth(): UseAuthResult {
  const { instance, accounts, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();

  const user = useMemo<AuthUser | null>(() => {
    const active = instance.getActiveAccount() ?? accounts[0];
    if (!active) return null;
    const name = active.name ?? '';
    const username = active.username ?? '';
    return {
      name: name || username,
      username,
      initials: buildInitials(name, username),
      homeAccountId: active.homeAccountId,
      npi: npiFromClaims(active.idTokenClaims as Record<string, unknown> | undefined),
      account: active,
    };
  }, [accounts, instance]);

  const signIn = useCallback(
    async (overrides: Partial<RedirectRequest> = {}) => {
      await instance.loginRedirect({ ...loginRequest, ...overrides });
    },
    [instance],
  );

  const signOut = useCallback(async () => {
    const account = instance.getActiveAccount() ?? accounts[0];
    // The next sign-in in this tab must attest again (PLA-1110).
    clearAttested();
    await instance.logoutRedirect({
      account,
      postLogoutRedirectUri: appConfig.entraPostLogoutRedirectUri,
    });
  }, [accounts, instance]);

  return {
    isAuthenticated,
    isInteractionInProgress: inProgress !== InteractionStatus.None,
    user,
    signIn,
    signOut,
  };
}
